/**
 * Validates a Romanian CUI or CNP.
 * CUI: 1-10 digits, optional "RO" prefix
 * CNP: exactly 13 digits, starts with 1-8
 */

export type CuiCnpType = "cui" | "cnp" | "unknown";

export function detectCuiCnpType(value: string): CuiCnpType {
  const trimmed = value.trim();
  if (!trimmed) return "unknown";

  // Strip RO prefix → definitely a CUI
  const stripped = trimmed.replace(/^RO/i, "");
  if (stripped !== trimmed) return "cui";

  // Pure digits
  const digits = stripped.replace(/\s/g, "");
  if (!/^\d+$/.test(digits)) return "unknown";

  if (digits.length === 13 && /^[1-8]/.test(digits)) return "cnp";
  if (digits.length >= 1 && digits.length <= 10) return "cui";

  return "unknown";
}

export function validateCuiCnp(value: string): { valid: boolean; error?: string; type: CuiCnpType } {
  const trimmed = value.trim();
  if (!trimmed) return { valid: true, type: "unknown" }; // empty is OK (optional field)

  const type = detectCuiCnpType(trimmed);
  const stripped = trimmed.replace(/^RO/i, "").replace(/\s/g, "");

  if (type === "cui") {
    if (!/^\d{1,10}$/.test(stripped)) {
      return { valid: false, error: "CUI/CIF: maxim 10 cifre (opțional prefix RO)", type };
    }
    return { valid: true, type };
  }

  if (type === "cnp") {
    if (!/^[1-8]\d{12}$/.test(stripped)) {
      return { valid: false, error: "CNP: exact 13 cifre, începe cu 1-8", type };
    }
    return { valid: true, type };
  }

  // Unknown — too many digits or invalid chars
  if (/^\d+$/.test(stripped)) {
    if (stripped.length > 13) {
      return { valid: false, error: "Prea multe cifre. CUI/CIF: max 10, CNP: exact 13", type };
    }
    if (stripped.length > 10 && stripped.length < 13) {
      return { valid: false, error: "Format invalid. CUI/CIF: max 10 cifre, CNP: exact 13 cifre", type };
    }
  }

  return { valid: false, error: "Format invalid (cifre, opțional prefix RO)", type };
}
